import { Check as CheckIcon, Warning as WarningIcon } from "@mui/icons-material";
import { Box, Tooltip, Typography } from "@mui/material";

interface OverlayScore {
  score: number;
  label: string;
}

interface OverlayScoreMarkerProps extends OverlayScore {
  maxScore: number;
  color: string;
}

interface ScoreBarProps {
  score: number;
  showRange?: boolean;
  minScore?: number;
  maxScore?: number;
  scoreRange?: [number, number];
  overlayScores?: OverlayScore[];
  overlayTopScores?: OverlayScore[];
  count?: number;
  stddev?: number;
  showConfidenceInterval?: boolean;
}

export const CONFIDENCE_INTERVAL_THRESHOLD: number = 0.3;
export const MIN_SCORE_COUNT: number = 5;
const CONFIDENCE_MULTIPLIER: number = 1.96;

export const SCORE_COLORS = {
  low: { background: "#E57373", border: "#E57373", indicatorColor: "#F44336", fontColor: "#FFFFFF" }, // Lighter red background, dark red indicator (0-1)
  medium: { background: "#FFCC80", border: "#FFCC80", indicatorColor: "#ff5722", fontColor: "#000000" }, // Light orange background, medium orange indicator (2-3)
  veryHigh: { background: "#AED581", border: "#AED581", indicatorColor: "#66CD00", fontColor: "#FFFFFF" } // Lighter green background, medium green indicator (4-5)
};

export const hasScoreWarning = (count: number, stddev: number): boolean => {
  if (count <= 0) {
    return true;
  }

  const confidenceInterval = stddev / Math.sqrt(count) * CONFIDENCE_MULTIPLIER;
  return confidenceInterval > CONFIDENCE_INTERVAL_THRESHOLD || count <= MIN_SCORE_COUNT;
};

const LOW_SCORE_THRESHOLD: number = 1.67;
const MEDIUM_SCORE_THRESHOLD: number = 3.34;

export const getScoreChipColor = (score: number): Record<string, string> => {
  if (score < LOW_SCORE_THRESHOLD) {
    return SCORE_COLORS.low;
  }
  if (score < MEDIUM_SCORE_THRESHOLD) {
    return SCORE_COLORS.medium;
  }
  return SCORE_COLORS.veryHigh;
};

const getConfidenceIntervalText = (count: number, confidenceInterval: number): string => {
  if (count < MIN_SCORE_COUNT) {
    return `Based on only ${count} scored. Don't overinterpret this average.`;
  }

  if (confidenceInterval > CONFIDENCE_INTERVAL_THRESHOLD) {
    return `${count} scored, 95% CI ~${confidenceInterval?.toFixed(2)} - high variance, consider other factors.`;
  }

  return `${count} scored, 95% CI ~${confidenceInterval?.toFixed(2)} - fairly reliable`;
};

const OverlayScoreMarker = ({ score, label, maxScore, color }: OverlayScoreMarkerProps) => {
  const scorePercentage = Math.min(Math.max(score / maxScore, 0), 1) * 100;
  return (
    <Tooltip
      title={`${label}: ${score?.toFixed(2)}`}
      placement="top"
      arrow
    >
      <Box
        sx={{
          position: "absolute",
          left: `calc(${scorePercentage}% - 2.5px)`,
          top: -3,
          width: 4,
          height: 21,
          backgroundColor: color,
          border: "1px solid #FFF",
          borderRadius: 1,
          zIndex: 1,
          "&:hover": {
            transform: "scaleX(2) scaleY(1.2)", // Slightly wider on hover
            boxShadow: "0 2px 6px rgba(0,0,0,0.3)" // Enhanced shadow
          }
        }}
      />
    </Tooltip>
  );
};

const OverlayTopScoreMarker = ({ score, label, maxScore, color }: OverlayScoreMarkerProps) => {
  const scorePercentage = Math.min(Math.max(score / maxScore, 0), 1) * 100;
  return (
    <Tooltip title={`${label}: ${score?.toFixed(2)}`} placement="top" arrow>
      <Box
        sx={{
          position: "absolute",
          left: `calc(${scorePercentage}% - 5px)`,
          top: 2,
          width: 10,
          height: 10,
          backgroundColor: color,
          border: "1px solid #FFF",
          borderRadius: 3,
          zIndex: 1,
          "&:hover": {
            transform: "scaleX(1.25) scaleY(1.25)", // Slightly wider on hover
            boxShadow: "0 2px 6px rgba(0,0,0,0.3)" // Enhanced shadow
          }
        }} />
    </Tooltip>
  );
};

export const ScoreBar = ({ score, showRange = false, scoreRange = [0, 5], minScore = 0, maxScore = 5, overlayScores = [], overlayTopScores = [], count = 0, stddev = 0, showConfidenceInterval = false }: ScoreBarProps) => {
  const scorePercentage = Math.min(Math.max(score / scoreRange[1], 0), 1) * 100;
  const minScorePercentage = Math.min(Math.max(minScore / scoreRange[1], 0), 1) * 100;
  const maxScorePercentage = Math.min(Math.max(maxScore / scoreRange[1], 0), 1) * 100;
  const chipColor = getScoreChipColor(score);

  const dividerMarks = [1, 2, 3, 4];
  const dividerPositions = dividerMarks.map(mark => (mark / scoreRange[1]) * 100);

  const confidenceInterval = showConfidenceInterval && count > 0
    ? stddev / Math.sqrt(count) * CONFIDENCE_MULTIPLIER
    : 0;
  const isConfidenceIntervalStable = confidenceInterval <= CONFIDENCE_INTERVAL_THRESHOLD && count > MIN_SCORE_COUNT;
  const confidenceIntervalText = getConfidenceIntervalText(count, confidenceInterval);

  return (
    <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
      <Box sx={{
        position: "relative",
        width: "100%",
        height: 16,
        border: "1px solid #CCC",
        borderRadius: 1,
        overflow: "visible", // Changed to visible to allow tooltip
        backgroundColor: "#F8F8F8"
      }}>
        {!showRange && (<Box
          sx={{
            position: "absolute",
            height: "100%",
            width: `${scorePercentage}%`,
            backgroundColor: chipColor.background,
            borderRight: scorePercentage < 100 ? `1px solid ${chipColor.border}` : "none"
          }}
        />
        )}
        {showRange && (
          <Tooltip
            title={`${minScore?.toFixed(2)} - ${maxScore?.toFixed(2)}`}
            placement="top"
            arrow
          >
            <Box
              sx={{
                position: "absolute",
                height: "100%",
                borderRadius: 1,
                left: `${minScorePercentage}%`,
                right: `${maxScorePercentage}%`,
                width: `${maxScorePercentage - minScorePercentage}%`,
                backgroundColor: chipColor.background,
                borderRight: scorePercentage < 100 ? `1px solid ${chipColor.border}` : "none"
              }}
            />
          </Tooltip>
        )}
        {/* Divider lines at 1, 2, 3, 4 marks */}
        {dividerPositions.map((position, index) => (
          <Box
            key={dividerMarks[index]}
            sx={{
              position: "absolute",
              left: `${position}%`,
              top: 0,
              width: "1px",
              height: "100%",
              backgroundColor: "#D8D8D8",
              zIndex: 0
            }}
          />
        ))}
        {overlayScores.map((overlayScore) => (
          <OverlayScoreMarker key={overlayScore.label} score={overlayScore.score} label={overlayScore.label} maxScore={scoreRange[1]} color={getScoreChipColor(overlayScore.score).indicatorColor}/>
        ))}
        {overlayTopScores.map((overlayTopScore) => (
          <OverlayTopScoreMarker key={overlayTopScore.label} score={overlayTopScore.score} label={overlayTopScore.label} maxScore={scoreRange[1]} color={getScoreChipColor(overlayTopScore.score).indicatorColor}/>
        ))}
      </Box>
      <Typography variant="caption" sx={{ ml: 1, pt: 0.25, fontSize: 12, minWidth: 30, fontWeight: 600 }}>
        {score?.toFixed(2)}
      </Typography>
      {showConfidenceInterval && !isConfidenceIntervalStable && (
        <Tooltip title={confidenceIntervalText}>
          <WarningIcon sx={{ fontSize: 16, color: "warning.main" }}/>
        </Tooltip>
      )}
      {showConfidenceInterval && isConfidenceIntervalStable && (
        <Tooltip title={confidenceIntervalText}>
          <CheckIcon sx={{ fontSize: 16, color: "success.main" }}/>
        </Tooltip>
      )}
    </Box>
  );
};
